import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../api/client';
import { CREDITS_UPDATED_EVENT } from './CreditDisplay';
import GlassCard from './ui/GlassCard';

/**
 * TrialCreditsSection - Comprehensive credit breakdown for trial users
 * Shows all remaining credits with visual progress bars
 */
const TrialCreditsSection = () => {
    const navigate = useNavigate();
    const [creditData, setCreditData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchCredits = useCallback(async () => {
        try {
            const data = await apiEndpoints.auth.getCredits();
            setCreditData(data);
        } catch (error) {
            console.error('Error fetching credits:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCredits();

        // Listen for credit update events
        const handleCreditsUpdated = () => {
            fetchCredits();
        };

        window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);

        return () => {
            window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
        };
    }, [fetchCredits]);

    // Credit categories configuration
    const creditCategories = [
        {
            key: 'ai_resumes',
            label: 'AI Resume Generation',
            description: 'Generate tailored resumes from job descriptions',
            maxCredits: 5,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <rect x="4" y="2" width="16" height="20" rx="2" stroke="url(#resume-grad)" strokeWidth="2"/>
                    <path d="M8 6h8M8 10h8M8 14h4" stroke="url(#resume-grad)" strokeWidth="1.5" strokeLinecap="round"/>
                    <defs>
                        <linearGradient id="resume-grad" x1="4" y1="2" x2="20" y2="22">
                            <stop offset="0%" stopColor="var(--indigo-500)"/>
                            <stop offset="100%" stopColor="var(--cyan-400)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--indigo-500) 0%, var(--cyan-400) 100%)',
        },
        {
            key: 'ai_copilot',
            label: 'AI Copilot Sessions',
            description: 'Get AI help with resume writing and editing',
            maxCredits: 5,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M12 3l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6l2-6z" stroke="url(#copilot-grad)" strokeWidth="2" strokeLinejoin="round"/>
                    <defs>
                        <linearGradient id="copilot-grad" x1="4" y1="3" x2="20" y2="21">
                            <stop offset="0%" stopColor="var(--cyan-400)"/>
                            <stop offset="100%" stopColor="var(--indigo-500)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--cyan-400) 0%, var(--indigo-500) 100%)',
        },
        {
            key: 'mock_interviews',
            label: 'Mock Interviews',
            description: 'Practice with AI-powered interview simulations',
            maxCredits: 3,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <circle cx="12" cy="8" r="4" stroke="url(#interview-grad)" strokeWidth="2"/>
                    <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="url(#interview-grad)" strokeWidth="2" strokeLinecap="round"/>
                    <defs>
                        <linearGradient id="interview-grad" x1="6" y1="4" x2="18" y2="21">
                            <stop offset="0%" stopColor="var(--amber-500)"/>
                            <stop offset="100%" stopColor="var(--indigo-500)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--amber-500) 0%, var(--indigo-500) 100%)',
        },
        {
            key: 'star_stories',
            label: 'STAR Stories',
            description: 'Generate structured interview stories',
            maxCredits: 3,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M12 2l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9l3-9z" stroke="url(#star-grad)" strokeWidth="2" strokeLinejoin="round"/>
                    <defs>
                        <linearGradient id="star-grad" x1="2" y1="2" x2="22" y2="22">
                            <stop offset="0%" stopColor="var(--amber-400)"/>
                            <stop offset="100%" stopColor="var(--amber-500)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--amber-400) 0%, var(--amber-500) 100%)',
        },
        {
            key: 'company_research',
            label: 'Company Research',
            description: 'Get AI-powered company insights',
            maxCredits: 3,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <rect x="3" y="8" width="18" height="13" rx="2" stroke="url(#research-grad)" strokeWidth="2"/>
                    <path d="M7 8V5a2 2 0 012-2h6a2 2 0 012 2v3" stroke="url(#research-grad)" strokeWidth="2"/>
                    <circle cx="12" cy="14" r="2" stroke="url(#research-grad)" strokeWidth="2"/>
                    <defs>
                        <linearGradient id="research-grad" x1="3" y1="3" x2="21" y2="21">
                            <stop offset="0%" stopColor="var(--indigo-400)"/>
                            <stop offset="100%" stopColor="var(--cyan-500)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--indigo-400) 0%, var(--cyan-500) 100%)',
        },
        {
            key: 'resume_walkthroughs',
            label: 'Resume Walkthroughs',
            description: 'Practice explaining your resume',
            maxCredits: 3,
            icon: (
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '100%', height: '100%' }}>
                    <path d="M9 18l6-6-6-6" stroke="url(#walkthrough-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="10" stroke="url(#walkthrough-grad)" strokeWidth="2"/>
                    <defs>
                        <linearGradient id="walkthrough-grad" x1="2" y1="2" x2="22" y2="22">
                            <stop offset="0%" stopColor="var(--green-500)"/>
                            <stop offset="100%" stopColor="var(--cyan-400)"/>
                        </linearGradient>
                    </defs>
                </svg>
            ),
            gradient: 'linear-gradient(135deg, var(--green-500) 0%, var(--cyan-400) 100%)',
        },
    ];

    const styles = {
        section: {
            marginTop: 'var(--space-2xl)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-lg)',
        },
        title: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
        },
        subtitle: {
            fontSize: '0.875rem',
            color: 'var(--text-tertiary)',
            marginTop: 'var(--space-xs)',
        },
        upgradeBtn: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            background: 'var(--gradient-primary)',
            color: '#fff',
            padding: 'var(--space-sm) var(--space-lg)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'transform var(--duration-fast), box-shadow var(--duration-fast)',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-lg)',
        },
        card: {
            padding: 'var(--space-lg)',
        },
        cardHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            marginBottom: 'var(--space-md)',
        },
        iconWrapper: {
            width: '40px',
            height: '40px',
            padding: 'var(--space-sm)',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        cardInfo: {
            flex: 1,
        },
        cardTitle: {
            fontWeight: 600,
            fontSize: '0.95rem',
            color: 'var(--text-primary)',
            marginBottom: '2px',
        },
        cardDescription: {
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
        },
        progressContainer: {
            marginTop: 'var(--space-sm)',
        },
        progressHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-xs)',
        },
        creditsText: {
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: '1.1rem',
            color: 'var(--text-primary)',
        },
        creditsMax: {
            fontSize: '0.8rem',
            color: 'var(--text-tertiary)',
        },
        progressBar: {
            height: '8px',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
        },
        progressFill: {
            height: '100%',
            borderRadius: 'var(--radius-full)',
            transition: 'width 0.5s ease-out',
        },
        emptyState: {
            textAlign: 'center',
            padding: 'var(--space-2xl)',
            color: 'var(--text-tertiary)',
        },
        totalCredits: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-lg)',
            padding: 'var(--space-xl)',
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            marginTop: 'var(--space-xl)',
        },
        totalNumber: {
            fontFamily: 'var(--font-display)',
            fontSize: '3rem',
            fontWeight: 700,
            background: 'var(--gradient-text)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        totalLabel: {
            fontSize: '1rem',
            color: 'var(--text-secondary)',
        },
    };

    if (loading) {
        return (
            <div style={styles.section}>
                <div style={styles.emptyState}>Loading credits...</div>
            </div>
        );
    }

    // Don't show for premium users or if credit data is missing
    if (!creditData || creditData.is_premium || !creditData.credits) {
        return null;
    }

    const { credits, tier } = creditData;

    // Calculate total remaining
    const totalRemaining = creditCategories.reduce(
        (sum, cat) => sum + (credits[cat.key] || 0),
        0
    );
    const totalMax = creditCategories.reduce((sum, cat) => sum + cat.maxCredits, 0);

    return (
        <div style={styles.section}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <h2 style={styles.title}>
                        {tier === 'trial' ? 'Your Trial Credits' : 'Your Credits'}
                    </h2>
                    <p style={styles.subtitle}>
                        {tier === 'trial'
                            ? 'Explore all features during your trial period'
                            : 'Upgrade to Pro for unlimited access'}
                    </p>
                </div>
                <button
                    style={styles.upgradeBtn}
                    onClick={() => navigate('/upgrade')}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                        <path d="M10 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6l2-6z"/>
                    </svg>
                    Upgrade to Pro
                </button>
            </div>

            {/* Credits Grid */}
            <div style={styles.grid}>
                {creditCategories.map((category) => {
                    const remaining = credits[category.key] || 0;
                    const percentage = (remaining / category.maxCredits) * 100;
                    const isEmpty = remaining === 0;

                    return (
                        <GlassCard key={category.key} style={styles.card}>
                            <div style={styles.cardHeader}>
                                <div style={styles.iconWrapper}>
                                    {category.icon}
                                </div>
                                <div style={styles.cardInfo}>
                                    <div style={styles.cardTitle}>{category.label}</div>
                                    <div style={styles.cardDescription}>{category.description}</div>
                                </div>
                            </div>

                            <div style={styles.progressContainer}>
                                <div style={styles.progressHeader}>
                                    <span style={{
                                        ...styles.creditsText,
                                        color: isEmpty ? 'var(--red-500)' : 'var(--text-primary)',
                                    }}>
                                        {remaining}
                                    </span>
                                    <span style={styles.creditsMax}>
                                        of {category.maxCredits} remaining
                                    </span>
                                </div>
                                <div style={styles.progressBar}>
                                    <div
                                        style={{
                                            ...styles.progressFill,
                                            width: `${percentage}%`,
                                            background: isEmpty ? 'var(--red-500)' : category.gradient,
                                        }}
                                    />
                                </div>
                            </div>
                        </GlassCard>
                    );
                })}
            </div>

            {/* Total Credits Summary */}
            <div style={styles.totalCredits}>
                <div style={{ textAlign: 'center' }}>
                    <div style={styles.totalNumber}>{totalRemaining}</div>
                    <div style={styles.totalLabel}>Total Credits Remaining</div>
                </div>
                <div style={{ width: '1px', height: '60px', background: 'var(--border-light)' }} />
                <div style={{ textAlign: 'center' }}>
                    <div style={{ ...styles.totalNumber, opacity: 0.5 }}>{totalMax}</div>
                    <div style={styles.totalLabel}>Maximum Trial Credits</div>
                </div>
            </div>
        </div>
    );
};

export default TrialCreditsSection;
